import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { PageSeo } from '@/components/seo/PageSeo';
import {
  Globe,
  ArrowRight,
  MessageSquare,
  Smartphone,
  Bell,
  MapPin,
  Calendar,
  Users,
  Sparkles,
  Shield,
  CreditCard,
  RefreshCw,
  Bot,
  Home,
  Building2,
  Castle,
} from 'lucide-react';
import { Button } from '@/components/ui';

export interface AudienceContent {
  meta: { title: string; description: string };
  hero: { badge: string; title: string; highlight: string; subtitle: string; image: string };
  painPoints: { title: string; items: string[] };
  solutions: { title: string; subtitle: string; items: { icon: string; title: string; desc: string }[] };
  stats: { value: string; label: string }[];
  cta: { title: string; subtitle: string; button: string };
}

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  messages: MessageSquare, smartphone: Smartphone, bell: Bell, map: MapPin,
  calendar: Calendar, users: Users, sparkles: Sparkles, shield: Shield,
  creditcard: CreditCard, sync: RefreshCw, bot: Bot, globe: Globe,
  home: Home, building: Building2, castle: Castle,
};

const gradients = [
  'from-cyan-500 to-blue-500', 'from-blue-500 to-indigo-500',
  'from-emerald-500 to-teal-500', 'from-amber-500 to-orange-500',
  'from-rose-500 to-pink-500', 'from-violet-500 to-purple-500',
];

export default function TargetAudiencePage({ content, canonical }: { content: { hr: AudienceContent; en: AudienceContent }; canonical: string }) {
  const { i18n } = useTranslation();
  const lang = (i18n.language === 'hr' ? 'hr' : 'en') as 'hr' | 'en';
  const t = content[lang];
  const toggleLanguage = () => i18n.changeLanguage(lang === 'hr' ? 'en' : 'hr');

  return (
    <>
      <PageSeo path={canonical} title={t.meta.title} description={t.meta.description} />

      <div className="min-h-screen bg-[hsl(var(--background))]">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-[hsl(var(--background))]/80 backdrop-blur-xl border-b border-[hsl(var(--border))]/50">
          <div className="container mx-auto flex items-center justify-between px-4 py-3">
            <Link to="/" className="flex items-center">
              <img src="/favicon-original.png" alt="BookiApp" className="h-8 w-auto" />
            </Link>
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={toggleLanguage} className="px-2">
                <Globe className="h-4 w-4 mr-1" />{lang.toUpperCase()}
              </Button>
              <Link to="/pricing">
                <Button size="sm" className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white border-0">{t.cta.button}</Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Hero */}
        <section className="pt-16 pb-12 md:pt-24 md:pb-16 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-[40%] -left-[20%] w-[80%] h-[80%] bg-gradient-to-br from-cyan-400/20 via-blue-400/10 to-transparent rounded-full blur-[100px]" />
          </div>
          <div className="container mx-auto px-4 relative text-center">
            <span className="inline-block px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-600 text-xs font-medium mb-6">{t.hero.badge}</span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight mb-6 leading-[1.1]">
              {t.hero.title}{' '}
              <span className="bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">{t.hero.highlight}</span>
            </h1>
            <p className="text-lg md:text-xl text-[hsl(var(--muted-foreground))] max-w-3xl mx-auto leading-relaxed">{t.hero.subtitle}</p>
            <div className="text-6xl mt-8">{t.hero.image}</div>
          </div>
        </section>

        {/* Pain Points */}
        <section className="py-16 md:py-20 bg-[hsl(var(--muted))]/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">{t.painPoints.title}</h2>
            <div className="max-w-2xl mx-auto space-y-4">
              {t.painPoints.items.map((item, i) => (
                <div key={i} className="flex items-start gap-3 bg-[hsl(var(--card))] rounded-xl p-4 border border-red-500/10">
                  <span className="text-red-500 mt-0.5 text-lg">✗</span>
                  <span className="text-[hsl(var(--foreground))]">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Solutions */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">{t.solutions.title}</h2>
              <p className="text-lg text-[hsl(var(--muted-foreground))]">{t.solutions.subtitle}</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {t.solutions.items.map((item, i) => {
                const Icon = iconMap[item.icon] || Sparkles;
                return (
                  <div key={i} className="group bg-[hsl(var(--card))] rounded-2xl border border-[hsl(var(--border))]/50 p-6 hover:border-cyan-500/20 hover:shadow-lg transition-all duration-300">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradients[i % gradients.length]} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 bg-[hsl(var(--muted))]/30">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-center gap-8 md:gap-16">
              {t.stats.map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">{stat.value}</div>
                  <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto relative rounded-3xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 via-blue-600 to-indigo-600" />
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,255,255,0.15),transparent_50%)]" />
              <div className="relative px-8 py-14 md:px-16 md:py-20 text-center text-white">
                <h2 className="text-3xl sm:text-4xl font-extrabold mb-4">{t.cta.title}</h2>
                <p className="text-lg opacity-90 mb-8 max-w-xl mx-auto">{t.cta.subtitle}</p>
                <Link to="/pricing">
                  <Button size="lg" className="bg-white text-blue-600 hover:bg-white/90 shadow-lg text-base px-8 h-12 rounded-xl group font-bold">
                    {t.cta.button}
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <div className="text-center pb-12">
          <Link to="/" className="text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">
            &larr; {lang === 'hr' ? 'Povratak na početnu' : 'Back to home'}
          </Link>
        </div>
      </div>
    </>
  );
}
